import { ReflowRow } from 'editor/src/store/design/types';
import { getReflowMemberVerticalBounds, ReflowMember } from 'editor/src/store/design/util/reflowMember';

/**
 * Group reflow-eligible elements (text, and now line/rectangle/image) into
 * reflow rows by y-proximity.
 *
 * Two elements belong to the same row if their vertical bounding boxes
 * overlap — i.e. they're visually on the same line on the canvas. A "Phone:"
 * label at y=22 and a "0000000" value at y=22 end up in one row; a Name field
 * at y=10 and a Phone field at y=22 (no overlap) end up in separate rows. The
 * result is sorted top-to-bottom so reflow's declared-order walk matches the
 * visual stack.
 *
 * Pure; no side effects. Used by createReflowGroupFromSelectionOperation when
 * the author multi-selects elements on the canvas and clicks "Add reflow".
 */
function clusterIntoRows(elements: ReflowMember[]): ReflowRow[] {
  if (elements.length === 0) {
    return [];
  }

  // Deterministic row ids derived from the contained uuids → same input
  // produces the same ids, so re-running on a stable selection is idempotent
  // and re-creates the same rows.
  const rowId = (uuids: number[]): string => `rr-${[...uuids].sort((a, b) => a - b).join('-')}`;

  // Sort by top y so we can sweep top→bottom and grow each cluster greedily.
  const sorted = [...elements].sort(
    (a, b) => getReflowMemberVerticalBounds(a).top - getReflowMemberVerticalBounds(b).top,
  );

  const clusters: ReflowMember[][] = [];
  sorted.forEach((element) => {
    const { top: elementTop, bottom: elementBottom } = getReflowMemberVerticalBounds(element);

    const target = clusters.find((cluster) => {
      const clusterTop = Math.min(...cluster.map((e) => getReflowMemberVerticalBounds(e).top));
      const clusterBottom = Math.max(...cluster.map((e) => getReflowMemberVerticalBounds(e).bottom));
      // Any overlap in the [top, bottom] ranges puts them on the same visual line.
      return elementTop < clusterBottom && elementBottom > clusterTop;
    });

    if (target) {
      target.push(element);
    } else {
      clusters.push([element]);
    }
  });

  return clusters
    .map((cluster) => ({
      cluster,
      top: Math.min(...cluster.map((e) => getReflowMemberVerticalBounds(e).top)),
    }))
    .sort((a, b) => a.top - b.top)
    .map(({ cluster }) => ({
      id: rowId(cluster.map((e) => e.uuid)),
      optional: true,
      elementUuids: cluster.map((e) => e.uuid),
    }));
}

export default clusterIntoRows;
